import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  actionSetRecentActivitySets,
  actionSetRecentOperatorRuns,
  actionSetCurrentWorkOrderOp,
  actionOptimisticActivityUpdate,
  actionOptimisticActivitySetUpdate,
  actionOpenClockOutModal,
  actionCanonicalActivitySwitch,
  actionOptimisticActivitySwitch,
  actionCanonicalActivityUpdate,
  actionCanonicalActivitySetUpdate,
  actionSetLaborTickets,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getLatestActivityRef } from 'lib/selectors/getLatestActivityRef';
import { getMachine } from 'lib/selectors/getMachine';
import { getUnattendedOperatorPersonId } from 'lib/selectors/getUnattendedOperatorPersonId';
import { getClockOutFlowEnabled } from 'lib/selectors/getClockOutFlowEnabled';
import { useRequestRecentActivitySets } from 'lib/hooks';
import { throwCriticalErrorToast } from 'lib/utils/toast';

/**
 * Hook that handles labor ticket submit responses from embedded forms.
 * Encapsulates all the action dispatching logic for submit events.
 *
 * @param {Function} closeModal - Callback to close the modal (dispatches close action)
 * @returns {Function} handleSubmit - Function to call with submit event data
 */
const useHandleLaborTicketSubmit = (closeModal) => {
  const dispatch = useDispatch();
  const { refetch: refetchRecentActivitySets } = useRequestRecentActivitySets();
  const latestActivitySet = useSelector(getLatestActivitySet);
  const machine = useSelector(getMachine);
  const latestActivityRef = useSelector(getLatestActivityRef);
  const unattendedOperatorPersonId = useSelector(getUnattendedOperatorPersonId);
  const currentOperator = useSelector(getCurrentOperator);
  const clockOutFlowEnabled = useSelector(getClockOutFlowEnabled);

  const handleSubmit = useCallback(
    (data) => {
      // Check for failure response (success: false indicates ERP/backend error)
      if (data.success === false) {
        const errorMessage =
          data.message || data.error || 'Submission failed. Please try again.';
        throwCriticalErrorToast(errorMessage);
        closeModal();
        return;
      }

      // Support both response structures: data.actionResults.* or data.*
      const actionData = data.actionResults || data;

      const {
        updateActivitySet,
        stopOperatorRun,
        pauseSetup,
        completeSetup,
        closeLaborTicket,
        createProductionTicket,
        startOperatorRun,
      } = actionData;

      // Capture the current operator BEFORE any state changes happen
      const operatorBeforeSubmit = currentOperator;

      // Handle labor ticket actions
      // If we have a new production ticket (e.g., from completing setup), use that
      // Otherwise, handle the closed ticket
      if (createProductionTicket) {
        dispatch(actionSetLaborTickets([createProductionTicket]));
      } else if (closeLaborTicket) {
        if (closeLaborTicket.personId === unattendedOperatorPersonId) {
          // Clear all tickets if unattended operator
          dispatch(actionSetLaborTickets([]));
          dispatch(actionSetEmbeddedLaborTicket(null));
        } else {
          // Dispatch the closed ticket - reducer will set currentLaborTicket to null because state !== 'OPEN'
          dispatch(actionSetLaborTickets([closeLaborTicket]));
        }
      }

      if (updateActivitySet) {
        dispatch(actionSetRecentActivitySets([updateActivitySet.activitySet]));
        if (updateActivitySet.activitySet.closedAt) {
          dispatch(actionSetCurrentWorkOrderOp(null));
        }
      }

      if (startOperatorRun) {
        const { operator, machine: runMachine, ...rest } = startOperatorRun;
        dispatch(
          actionSetRecentOperatorRuns({
            operatorRuns: [
              {
                ...rest,
                operatorId: operator.id,
                machineId: runMachine.id,
              },
            ],
          })
        );
      }

      if (stopOperatorRun) {
        const { operator, ...rest } = stopOperatorRun;
        dispatch(
          actionSetRecentOperatorRuns({
            operatorRuns: [
              { ...rest, operatorId: operator.id, machineId: machine.id },
            ],
          })
        );
      }

      if (pauseSetup) {
        // Update the individual activity with the new endAt time
        dispatch(
          actionOptimisticActivityUpdate({
            activity: {
              activityRef: pauseSetup.activityRef,
              endAt: pauseSetup.endAt,
              end: pauseSetup.endAt,
              activitySetRef: latestActivitySet?.activitySetRef,
            },
          })
        );
        dispatch(
          actionCanonicalActivityUpdate({
            activity: {
              ...pauseSetup,
              end: pauseSetup.endAt,
            },
            activityRef: pauseSetup.activityRef,
          })
        );
        dispatch(
          actionOptimisticActivitySetUpdate({
            activitySet: {
              ...latestActivitySet,
              updatedAt: pauseSetup.endAt,
            },
          })
        );
        dispatch(
          actionCanonicalActivitySetUpdate({
            activitySet: {
              ...latestActivitySet,
              updatedAt: pauseSetup.endAt,
            },
            activitySetRef: latestActivitySet?.activitySetRef,
          })
        );
      }

      if (completeSetup) {
        // Update the individual activity with the full response from the API
        dispatch(
          actionOptimisticActivitySwitch({
            optimisticActivity: {
              ...completeSetup,
              start: completeSetup.startAt,
              end: completeSetup.endAt,
            },
            latestActivityRef,
          })
        );

        dispatch(
          actionCanonicalActivitySwitch({
            activity: {
              ...completeSetup,
              start: completeSetup.startAt,
              end: completeSetup.endAt,
            },
            activityRef: completeSetup.activityRef,
            optimisticAcitivityRef: null,
          })
        );
      }

      if (
        clockOutFlowEnabled &&
        stopOperatorRun &&
        stopOperatorRun.machine.id === machine.id
      ) {
        // Regular sign out - the operator info is in stopOperatorRun
        dispatch(actionOpenClockOutModal());
      }

      if (
        clockOutFlowEnabled &&
        startOperatorRun &&
        startOperatorRun.machineId === machine.id
      ) {
        // Unattended operator starting - use the operator from before submit
        // operatorBeforeSubmit should be the actual operator who submitted the form
        dispatch(actionOpenClockOutModal(operatorBeforeSubmit?.erpId));
      }

      // Close the modal and clean up
      closeModal();

      // Refetch activity sets to ensure state is in sync after embedded form submit
      refetchRecentActivitySets();
    },
    [
      dispatch,
      currentOperator,
      unattendedOperatorPersonId,
      latestActivitySet,
      machine,
      latestActivityRef,
      closeModal,
      refetchRecentActivitySets,
      clockOutFlowEnabled,
    ]
  );

  return handleSubmit;
};

export default useHandleLaborTicketSubmit;
